// Explore's analysis of a scope: the page reads the scope's traces as it lists
// them (who ran each, with what agent and model, for how long, at what cost,
// and what review found), folds them into a short pack of facts, and a model
// writes about the scope from those facts alone, as plain prose whose names
// are filters and whose traces are links. A follow-up question may also query
// the scope itself, through the analytics tools held to its traces.
//
// The answer's contract, which splitAnalysis reads while it streams:
//   - a first sentence: how many traces, from how many people, at what cost;
//   - one ```breakdown block (JSON): the scope split by its largest parts;
//   - a few short paragraphs, where [Name](filter:<clause>) is a person,
//     group, agent or model to filter by and [title](trace:<id>) a trace;
//   - one ```filters block: up to three lines of `label | clause`.
import { createAnalyticsQueryTool, createExpandPreviewTool, analyticsSchemaTool } from "./analyticsTool.js";
import { PROVIDER_TAG_TO_IDENTITY } from "./agentIdentity.js";
import { withScopeGuard } from "./scoutScope.js";
import { startSubAgent } from "./subAgent.js";

const SEV = ["critical", "high", "medium", "low"];
const NUM = new Intl.NumberFormat("en-US");
const TOP_PEOPLE = 12;
const TOP_GROUPS = 8;
const TOP_MODELS = 8;
const TOP_TRACES = 8;

/** The name an agent goes by: its provider's product name, else the tag. */
export function agentName(tag) {
  const t = String(tag || "").trim();
  return PROVIDER_TAG_TO_IDENTITY[t.toLowerCase()]?.name || t || "Unknown";
}

const round2 = (n) => Math.round(n * 100) / 100;

/** "1 min", "43 min", "2.1 hr", "4.6 days": from a trace's first request to its last in the range. */
export function durationText(startMs, lastMs) {
  if (!Number.isFinite(startMs) || !Number.isFinite(lastMs)) return "";
  const min = Math.max(0, lastMs - startMs) / 60_000;
  if (min < 59.5) return `${Math.max(1, Math.round(min))} min`;
  const hr = min / 60;
  if (hr < 23.95) return `${String(Math.round(hr * 10) / 10)} hr`;
  return `${(min / 1440).toFixed(1)} days`;
}

// "$4,899+": whole dollars from $100 up, cents below, "+" where it is a floor.
const moneyText = (usd, floor = false) => `${usd >= 100 ? `$${NUM.format(Math.round(usd))}` : `$${usd.toFixed(2)}`}${floor ? "+" : ""}`;

/**
 * The facts a scope's analysis is written from (pure).
 *   rows    the scope's traces as the page lists them (lib/sessionFacts.js)
 *   titles  id -> the trace's title (its newest prompt's summary), or null
 *   costs   id -> { usd, measured } (lib/traceCosts.js), null where unread
 *   total   how many traces the scope holds, where counted
 *   query   the search the scope is, as typed ("" for every trace)
 *   range   the range, as the page names it
 *   capped  whether the rows are only the newest of the scope's traces
 */
export function buildFacts({ rows = [], titles = new Map(), costs = new Map(), total = null, query = "", range = "", capped = false }) {
  const costOf = (id) => costs.get(id) || null;
  const titleOf = (id) => titles.get(id) || null;
  let usd = 0, priced = 0, partial = false;
  const findings = { critical: 0, high: 0, medium: 0, low: 0 };
  let flaggedTraces = 0;
  const people = new Map(), groups = new Map(), agents = new Map(), models = new Map();
  const bump = (map, key, init) => {
    if (!key) return null;
    if (!map.has(key)) map.set(key, { ...init });
    return map.get(key);
  };
  for (const r of rows) {
    const c = costOf(r.id);
    if (c?.usd != null) { usd += c.usd; priced += 1; if (!c.measured) partial = true; } else partial = true;
    const sevs = r.bySev || {};
    if (Object.values(sevs).some(Boolean)) flaggedTraces += 1;
    for (const s of SEV) findings[s] += sevs[s] || 0;
    const p = bump(people, r.person, { traces: 0, usd: 0, partial: false, high: 0, group: r.group || "" });
    if (p) {
      p.traces += 1;
      if (c?.usd != null) { p.usd += c.usd; if (!c.measured) p.partial = true; } else p.partial = true;
      p.high += (sevs.critical || 0) + (sevs.high || 0);
    }
    const g = bump(groups, r.group, { traces: 0 });
    if (g) g.traces += 1;
    const a = bump(agents, agentName(r.agent), { traces: 0, usd: 0 });
    if (a) { a.traces += 1; a.usd += c?.usd || 0; }
    const m = bump(models, r.model, { traces: 0 });
    if (m) m.traces += 1;
  }
  const n = rows.length;
  const share = (k) => (n ? round2(k / n) : 0);
  const trace = (r) => {
    const c = costOf(r.id);
    return {
      id: r.id,
      title: titleOf(r.id),
      person: r.person || null,
      group: r.group || null,
      agent: agentName(r.agent),
      model: r.model || null,
      duration: durationText(r.startMs, r.lastMs) || null,
      cost: c?.usd != null ? moneyText(c.usd, !c.measured) : null,
      findings: Object.fromEntries(SEV.filter((s) => r.bySev?.[s]).map((s) => [s, r.bySev[s]])),
    };
  };
  const worst = (r) => SEV.findIndex((s) => r.bySev?.[s]);
  const flagged = rows.filter((r) => worst(r) >= 0)
    .sort((a, b) => worst(a) - worst(b) || (b.findings || 0) - (a.findings || 0))
    .slice(0, TOP_TRACES);
  const costliest = rows.filter((r) => costOf(r.id)?.usd != null).sort((a, b) => costOf(b.id).usd - costOf(a.id).usd).slice(0, TOP_TRACES);
  const longest = rows.filter((r) => Number.isFinite(r.startMs) && Number.isFinite(r.lastMs)).sort((a, b) => (b.lastMs - b.startMs) - (a.lastMs - a.startMs)).slice(0, 6);
  const top = (map, k) => [...map.entries()].sort((a, b) => b[1].traces - a[1].traces).slice(0, k);
  return {
    scope: { search: query || "every trace", range, traces: total ?? (capped ? null : n), read: n, newestOnly: !!capped },
    cost: { total: moneyText(usd, partial), tracesPriced: priced },
    findings: { ...findings, traces: flaggedTraces },
    people: top(people, TOP_PEOPLE).map(([name, p]) => ({ name, group: p.group || null, traces: p.traces, share: share(p.traces), cost: p.usd ? moneyText(p.usd, p.partial) : null, highOrCritical: p.high })),
    peopleCount: people.size,
    groups: top(groups, TOP_GROUPS).map(([name, g]) => ({ name, traces: g.traces, share: share(g.traces) })),
    agents: top(agents, 10).map(([name, a]) => ({ name, traces: a.traces, share: share(a.traces), cost: a.usd ? moneyText(a.usd) : null })),
    models: top(models, TOP_MODELS).map(([name, m]) => ({ name, traces: m.traces })),
    traces: { flagged: flagged.map(trace), costliest: costliest.map(trace), longest: longest.map(trace) },
  };
}

export const ANALYSIS_SYSTEM_PROMPT = `You write the analysis of a scope of AI agent traces for Origin's Explore page. A trace is one conversation an agent ran for a person. The reader is a security or AI-governance lead deciding which traces deserve a look and which to filter out.

Write from the facts you are given. Every number you write must be in them or plainly computed from them. Never invent a trace, a person, an id or a value.

Write exactly this, in this order, as one reply that starts with its first sentence. Write nothing before it: no note that you are starting or have read the facts.
1. One sentence: how many traces, from how many people, costing how much ("1,717 traces from 14 people, costing $7,439+."). If the facts are only the newest traces of a larger scope, say so ("Of the newest 1,000 of 4,294 traces, ...").
2. One fenced block opened with \`\`\`breakdown holding JSON: {"title": "Traces by person" (or by group, agent or model, whichever split says most), "total": "<the traces it splits>", "rows": [up to 3 objects {"label", "value", "share" (0 to 1), "filter" (a clause, below)}], "rest": {"label": "<n> more people", "value", "share"}}. Values are display strings ("1,000", "763", "$2,450+").
3. Two to four short paragraphs, plain and specific: where the mass is (automated jobs, one person or agent doing most of the work), then what stands out past it (cost, long runs, high or critical findings, someone unusually quiet or busy), naming the traces that show it. Keep sentences short. Give each trace you name the one fact that makes it stand out (its cost, its findings or its duration), not all of them, and name at most three traces in a paragraph. Never write a count of zero. No headings, no lists.
4. One fenced block opened with \`\`\`filters: two or three lines, each "Label | clause", the filters most worth applying next, such as filtering out the mass to see the rest. Each label is "Filter for" or "Filter out" and the value it filters by ("Filter out Ada Park", "Filter for Security").

Costs and durations are display strings: write them exactly as the facts give them. Costs are estimates at public rates; one ending in "+" is a floor, so keep the "+".

Links:
- A person, group, agent or model you name is a link that filters by it: [Ada Park](filter:identity:"Ada Park"), [Security](filter:group:"Security"), [Claude](filter:agent:"Claude"), [opus-4.8](filter:model:"opus-4.8"). Link each name the first time you write it in a paragraph.
- A trace you name is a link with its title as written in the facts: [Migrating trace decoding from Python to Rust](trace:<its id>). Never link a trace that is not in the facts.
- Clauses are the search's own: identity:"<name>", group:"<group>", agent:"<agent>", model:"<model>", and a leading "-" to filter out (-identity:"<name>"). Several clauses in one filter are separated by spaces.

Words: say "traces", never "sessions". Say "cost", "duration", "filter for", "filter out". Refer to a person by name, never as he, she, his or her. Do not describe the screen or the reader's next steps beyond the filters. Do not call findings real, false or tests unless the facts say so. Write in ASCII: no em dashes, no middle dots, no emoji.`;

const FOLLOW_UP_PROMPT = `${ANALYSIS_SYSTEM_PROMPT}

Now the reader asks a question about the same scope. Answer it in the same voice and with the same links, in a few short paragraphs; skip the breakdown unless a split answers the question, and end with a filters block only where a filter would help. You may query the scope with the analytics tools to answer what the facts do not hold: call analytics_describe_schema before your first query, keep every query to the scope's traces, and use a grouped aggregate rather than reading rows.`;

/**
 * The task an analysis is given: the facts, and for a follow-up what was
 * written before and the question. `again` asks once more after a reply that
 * held only a note, not the analysis.
 */
export function analysisTask(facts, { question = null, earlier = "", again = false } = {}) {
  return [
    "FACTS (JSON):",
    JSON.stringify(facts),
    question && earlier ? `\nWHAT YOU WROTE BEFORE:\n${earlier}` : "",
    question ? `\nQUESTION:\n${question}` : "\nWrite the analysis.",
    again && !question ? "Your last reply was a note that you were starting, not the analysis. Write the whole analysis now, from its first sentence to its filters block." : "",
  ].filter(Boolean).join("\n");
}

const FENCE = /```(breakdown|filters)[^\n]*\n([\s\S]*?)(```|$)/g;
const TRACE_LINK = /\]\(trace:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\)/gi;

/**
 * An answer, as far as it has streamed, in its parts: the prose to render, the
 * breakdown once its block has closed and parses, and the filters it ends with.
 * A block still open is held back rather than shown half-written.
 */
export function splitAnalysis(text) {
  let breakdown = null;
  const filters = [];
  const prose = String(text || "").replace(FENCE, (_, kind, body, close) => {
    if (!close) return "";
    if (kind === "breakdown") {
      try {
        const v = JSON.parse(body);
        if (v && Array.isArray(v.rows)) breakdown = v;
      } catch { /* a malformed block shows nothing rather than its JSON */ }
      return "\n\n";
    }
    for (const line of body.split("\n")) {
      const at = line.indexOf("|");
      if (at < 0) continue;
      const label = line.slice(0, at).trim();
      const clause = line.slice(at + 1).split("|")[0].trim();
      if (label && clause) filters.push({ label, clause });
    }
    return "\n\n";
  });
  return { prose: prose.replace(/\n{3,}/g, "\n\n").trim(), breakdown, filters: filters.slice(0, 3) };
}

/** The traces an answer links, in the order it first names them (ids lowercased). */
export function traceRefs(text) {
  const out = [];
  for (const m of String(text || "").matchAll(TRACE_LINK)) {
    const id = m[1].toLowerCase();
    if (!out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * Start writing an analysis (or, with `question`, answering one) and stream it
 * through `onEvent` (the agent's events). The first analysis reads only the
 * facts, so it answers in one model call; a question may also query the scope,
 * with every query checked against its traces (`scopePath`).
 * -> the sub-agent's handle: { child, abort, promise }.
 */
export function startExploreAnalysis({ facts, question = null, earlier = "", again = false, scopePath = [], signal, onEvent }) {
  const getNamespaceKey = () => "explore-analysis";
  const tools = question
    ? [
      analyticsSchemaTool,
      withScopeGuard(createAnalyticsQueryTool({ getNamespaceKey }), { scopePath }),
      createExpandPreviewTool({ getNamespaceKey }),
    ]
    : [];
  return startSubAgent({
    role: "primary",
    signal,
    onEvent,
    tools,
    systemPrompt: question ? FOLLOW_UP_PROMPT : ANALYSIS_SYSTEM_PROMPT,
    task: analysisTask(facts, { question, earlier, again }),
  });
}
